import { useMemo } from "react";
import { useCalendar } from "../../../contexts/CalendarContext";
import { selectOpMode, selectOverview } from "../../../util/store/selectors";
import { useSelector } from "../../../util/store/store";
import { getStartFinishByOpMode } from "../../../util/varibles/global";
import { IOperation, ISite } from "../../../util/varibles/interface";

export interface ISiteLayout {
    site: ISite;
    lanes: IOperation[][];
    height: number;
    bottom: number;
}

export const OPERATION_LANE_STEP = 28;
export const OPERATION_HEIGHT = 20;
export const OPERATION_TOP_OFFSET = 33;

const buildOperationLanes = (operations: IOperation[], operationMode: ReturnType<typeof selectOpMode>) => {
    const sortedOperations = operations.map(operation => {
        const { start, finish } = getStartFinishByOpMode[operationMode](operation.activity_log, operation.operation.status);
        return { operation, start, finish };
    }).sort((a, b) => a.start - b.start || a.finish - b.finish || a.operation.operation.id.localeCompare(b.operation.operation.id));

    const lanes: IOperation[][] = [];
    const laneFinishTimes: number[] = [];

    sortedOperations.forEach(({ operation, start, finish }) => {
        const availableLane = laneFinishTimes.findIndex(laneFinish => laneFinish < start);
        const laneIndex = availableLane === -1 ? lanes.length : availableLane;

        if (!lanes[laneIndex])
            lanes[laneIndex] = [];

        lanes[laneIndex].push(operation);
        laneFinishTimes[laneIndex] = finish;
    });

    return lanes;
};

const useSiteLayout = (): ISiteLayout[] => {
    const sites = useSelector(selectOverview.sites);
    const operations = useSelector(selectOverview.opsSite);
    const operationMode = useSelector(selectOpMode);
    const [minHeight] = useCalendar(state => state.minHeight);

    return useMemo(() => {
        const operationsBySite = Object.keys(operations).reduce((result: { [siteId: string]: IOperation[] }, key) => {
            const operation = operations[key];
            const { site_ids = [] } = operation;

            site_ids.forEach(siteId => {
                result[siteId] = [...(result[siteId] || []), operation];
            });

            return result;
        }, {});

        let bottom = 60;
        return sites.map(site => {
            const lanes = buildOperationLanes(operationsBySite[site.id] || [], operationMode);
            const operationsHeight = lanes.length > 0
                ? (lanes.length - 1) * OPERATION_LANE_STEP + OPERATION_HEIGHT
                : 0;
            const height = Math.max(operationsHeight + OPERATION_TOP_OFFSET * 2, minHeight);
            bottom += height;

            return { site, lanes, height, bottom };
        });
    }, [minHeight, operationMode, operations, sites]);
};

export default useSiteLayout;
